<template>
  <!-- eslint-disable max-len -->
  <div
    :id="imageObj.id"
    class="grid-item-container"
  >
    <slot name="header" />
    <div
      :id="imageObj.id"
      class="grid-item"
      @mouseenter="$emit('image-mouseenter', $event)"
      @mouseleave="$emit('image-mouseleave', $event)"
    >
      <slot name="image" />
      <ImageCanvas
        :imageId="imageObj?.id"
        :boxes="boxes"
        :segmentations="segmentations"
        :crop="crop"
        :cropPadding="cropPadding"
        :imageQuality="imageQuality"
        :fetchPriority="fetchPriority"
      />
      <!-- Image Name Overlay -->
      <div v-if="imageObj?.name" class="image-name-overlay">
        {{ imageObj.name }}
      </div>
    </div>
    <slot name="content" />
  </div>
</template>

<script>
import ImageCanvas from '@/components/ImageCanvas.vue';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import { toRefs } from 'vue';
import { getColor, getAnnotationTypes } from '@/assets/js/utils.js';

export default {
  name: 'BaseGalleryGridItem',
  components: {
    ImageCanvas,
  },
  props: {
    imageObj: {
      type: Object,
      default: () => {},
    },
    annotations: {
      type: [Array, Object],
      default: () => [],
    },
    annotationSets: {
      type: Array,
      default: () => [],
    },
    crop: {
      type: Object,
      default: null,
    },
    cropPadding: {
      type: Number,
      default: 0,
    },
    annotationDisplayType: {
      type: String,
      default: 'set',
    },
    filterAnnotationsBySets: {
      type: Array,
      default: null,
    },
    filterAnnotationsByLabelIndexes: {
      type: Array,
      default: null,
    },
    imageQuality: {
      type: String,
      default: 'thumbnail',
    },
    fetchPriority: {
      type: String,
      default: 'auto',
    },
    labels: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'grid-item-clicked', 'image-deleted', 'annotation-list-updated', 'image-mouseenter', 'image-mouseleave',
  ],
  setup(props) {
    const { annotationSets } = toRefs(props);
    const { map: annotationSetColorMap } = useAnnotationColorMap({ items: annotationSets, key: 'id' });
    return {
      annotationSetColorMap,
    };
  },
  data() {
    return {};
  },
  computed: {
    filteredAnnotations() {
      let annotationsArray;
      if (Array.isArray(this.annotations)) {
        annotationsArray = this.annotations;
      } else if (typeof this.annotations === 'object') {
        // Convert object to array if it's not already
        annotationsArray = [this.annotations];
      } else {
        annotationsArray = [];
      }

      return annotationsArray;
    },
    boxes() {
      if (this.filteredAnnotations?.length > 0) {
        return this.filteredAnnotations.filter((anno) => getAnnotationTypes(anno).includes('box')).map((anno) => this.convertAnnotationToCanvasBox(anno));
      }
      return [];
    },
    segmentations() {
      if (this.filteredAnnotations?.length > 0) {
        return this.filteredAnnotations.filter((anno) => getAnnotationTypes(anno).includes('segmentation')).map((anno) => this.convertAnnotationToSegmentation(anno));
      }
      return [];
    },
  },
  methods: {
    handleImageClicked() {
      this.$emit('grid-item-clicked', this.imageObj);
    },
    convertAnnotationToCanvasBox(anno) {
      let originX = 0;
      let originY = 0;
      let x = anno.x;
      let y = anno.y;
      const w = anno.w;
      const h = anno.h;
      if (this.annotationSets) {
        const annoTask = this.annotationSets.find((set) => set.id === anno.annotation_set_id);
        if (annoTask) {
          if (annoTask.origin_x) {
            originX = annoTask.origin_x;
            x -= originX * anno.w;
          }
          if (annoTask.origin_y) {
            originY = annoTask.origin_y;
            y -= originY * anno.h;
          }
        }
      }
      const label = this.labels.find((e) => e.name === anno.label_name);
      let color;
      if (label && label.color) {
        color = this.parseColor(label.color);
      } else {
        if (this.annotationDisplayType === 'set' && anno.annotation_set_id) {
          color = this.annotationSetColorMap[anno.annotation_set_id];
        }
        if (this.annotationDisplayType === 'label' && anno.label_index >= 0) {
          color = getColor(anno.label_index);
        }
      }
      let opacity = 1;
      if (anno.opacity) {
        opacity = anno.opacity;
      }

      return {
        x, y, w, h, color, opacity,
      };
    },
    convertAnnotationToSegmentation(anno) {
      const polygons = JSON.parse(anno.polygon);

      const label = this.labels.find((e) => e.name === anno.label_name);
      let color;
      if (label && label.color) {
        color = this.parseColor(label.color);
      } else {
        if (this.annotationDisplayType === 'set' && anno.annotation_set_id) {
          color = this.annotationSetColorMap[anno.annotation_set_id];
        }
        if (this.annotationDisplayType === 'label' && anno.label_index >= 0) {
          color = getColor(anno.label_index);
        }
      }

      return {
        polygons, color,
      };
    },
    uint32ToHex(uint32Value) {
      if (typeof uint32Value !== 'number' || uint32Value < 0 || uint32Value > 0xFFFFFFFF || !Number.isInteger(uint32Value)) {
        throw new TypeError('Input must be a valid 32-bit unsigned integer');
      }

      // Convert the number to a hexadecimal string
      const hexString = uint32Value.toString(16);

      // Pad the string with leading zeros to ensure it has 8 characters
      return `#${hexString.padStart(8, '0').toUpperCase()}`;
    },
    parseColor(color) {
      if (typeof color === 'string') {
        let colorCode = color.slice(1);
        if (colorCode.length === 6) {
          colorCode += 'FF';
        }
        if (colorCode.length === 8) {
          colorCode = colorCode.slice(0, -2);
          colorCode += 'FF';
        }
        return colorCode;
      } else if (typeof color === 'number') {
        return this.uint32ToHex(color);
      } else {
        return null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.grid-item-container {
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 0;
  cursor: pointer;
  box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.125), 0 0 1px 1px rgba(0, 0, 0, 0.125);
  margin: 1px;
  transition: transform .4s ease;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  @include themify() {
    background: themed('image-background-fill');
    color: themed('text-color');
  }
}
.grid-item-container:hover {
  @include themify() {
    outline: 1px solid themed('card-list-hover-outline');
  }
}

.grid-item {
  display: flex;
  flex: 1 1 auto;
  position: relative;
  cursor: pointer;
  width: auto;
  height: 160px;
  max-height: 160px;
  min-height: 0;
  overflow: hidden;
}

.image-name-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  margin: 4px;
  background-color: rgba(255, 255, 0, 0.8); /* Yellow with 80% opacity */
  color: #000;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 10;
  max-width: 100%;
}
</style>
